import { useState } from 'react';
import {
  previewRegeneration,
  regenerateMission,
  type MissionView,
  type PlaceCandidateView,
  type RegenerateSummaryView,
} from '../api/missions';
import { ApiError } from '../api/http';
import { ca } from '../i18n/ca';
import { CandidatesMap } from './CandidatesMap';
import { messageOf } from './newMissionErrors';

const s = ca.script;

/**
 * The designer's «Mou» (release D, D11.8; release-d-E.md §2.4, §2.11): one
 * place the generator answered — a script's place, the base or the way out —
 * moved among its kind's own candidates, never to a free point (the owner,
 * 2026-09-28). A click on a candidate runs the regeneration as a dry run and
 * shows what it would move (the places measured from this one, the
 * alternatives written again, the hand edits it replaces, what the pre-play
 * check would move); «Confirma» runs it for real with the pin, as the next
 * revision. Nothing is stored before that.
 */
export function PlaceMover({
  code,
  revision,
  place,
  world,
  worldSize,
  names,
  onMoved,
  onFailed,
  onCancel,
}: {
  code: string;
  revision: number;
  place: { id: string; label: string };
  world: string;
  worldSize: number;
  /** A place id → the words the designer shows it by, for the summary's rows. */
  names: (id: string) => string;
  onMoved: (mission: MissionView) => void;
  /** An error the designer handles itself (a stale revision reloads it). */
  onFailed: (err: unknown) => void;
  onCancel: () => void;
}) {
  const [picked, setPicked] = useState<PlaceCandidateView | null>(null);
  const [summary, setSummary] = useState<RegenerateSummaryView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pins = (c: PlaceCandidateView): Record<string, [number, number]> => ({
    [place.id]: [c.x, c.y],
  });

  function show(err: unknown) {
    if (err instanceof ApiError && err.code === 'stale_revision') {
      onFailed(err);
      return;
    }
    setError(
      err instanceof ApiError && err.code === 'place_pin_refused' && err.detail
        ? err.detail
        : messageOf(err),
    );
  }

  async function pick(c: PlaceCandidateView) {
    setPicked(c);
    setSummary(null);
    setError(null);
    setBusy(true);
    try {
      setSummary(await previewRegeneration(code, revision, { pins: pins(c) }));
    } catch (err) {
      show(err);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!picked) return;
    setBusy(true);
    try {
      onMoved(await regenerateMission(code, revision, { pins: pins(picked) }));
    } catch (err) {
      show(err);
    } finally {
      setBusy(false);
    }
  }

  const metres = (from: number[] | null, to: number[]) =>
    from && from.length >= 2 ? Math.round(Math.hypot(to[0] - from[0], to[1] - from[1])) : null;
  // What the editor did not ask for is what needs reading: the places that
  // moved anyway, and the alternatives written again. A place kept is quiet.
  const rows = (summary?.places ?? []).filter((p) => p.outcome !== 'kept');

  return (
    <section className="card place-mover" aria-label={s.moveTitle(place.label)}>
      <div className="row">
        <h2 className="grow">{s.moveTitle(place.label)}</h2>
        <button type="button" onClick={onCancel} disabled={busy}>
          {s.moveCancel}
        </button>
      </div>
      <p className="muted small">{s.moveHint}</p>
      <CandidatesMap
        pick="place"
        world={world}
        mission={{ code, placeId: place.id, worldSize }}
        pickedPlace={picked}
        onPickPlace={(c) => void pick(c)}
        height={420}
      />
      {busy && (
        <p className="muted small" role="status">
          {summary ? s.regenerating : s.movePreviewing}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {summary && picked && (
        <div role="region" aria-label={s.moveSummary}>
          <h3>{s.moveSummary}</h3>
          <ul className="plain">
            {rows.map((p) => (
              <li key={p.id} className="small">
                {s.moveRow(
                  names(p.id),
                  s.moveOutcomes[p.outcome] ?? p.outcome,
                  metres(p.from, p.to),
                )}
                {p.why && <span className="muted block">{p.why}</span>}
              </li>
            ))}
          </ul>
          {summary.handEditsReplaced && <p className="notice warn small">{s.moveHandEdits}</p>}
          {summary.edits && summary.edits.carried > 0 && (
            <p className="muted small">{s.moveEditsCarried(summary.edits.carried)}</p>
          )}
          {(summary.edits?.dropped ?? []).map((d) => (
            <p key={`dropped:${d.path}`} className="notice warn small" title={d.path}>
              {s.moveEditDropped(d.why)}
            </p>
          ))}
          {(summary.edits?.texts ?? []).map((t) => (
            <p key={`text:${t.path}`} className="notice warn small" title={t.path}>
              {s.moveTextNames(t.names)}
            </p>
          ))}
          {summary.preflightMoved > 0 && (
            <p className="muted small">{s.movePreflight(summary.preflightMoved)}</p>
          )}
          <div className="row">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => void confirm()}
            >
              {s.moveConfirm}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
